import { encodeInWorker } from "../videoGif/frames";

import {
  type Decoration,
  LOOP_FRAMES,
  drawDecorated,
  isAnimated,
  isPlain,
  loopFps,
} from "./effects";
import {
  type Source,
  ANIMATED_SIZE,
  MAX_PIXELS,
  effectTime,
  frameAt,
  isAnimatedSource,
} from "./source";

/** Konkord: a still frame (no animation) keeps more pixels */
const STILL_SIZE = 512;
/** Konkord: frame rate of a GIF made over an animated picture */
const OVER_ANIMATION_FPS = 20;

/**
 * Konkord: the picked picture with its decoration, as the file to upload:
 * unchanged without one, a PNG for a still frame, a GIF when animated (the
 * decoration moves, or the picture itself is an animation)
 */
export async function renderDecorated(
  file: File | undefined,
  source: Source,
  decoration: Decoration,
  maxSize: number | undefined,
  signal?: AbortSignal,
): Promise<File> {
  const limit = maxSize ?? 4_000_000;
  if (isAnimatedSource(source))
    return renderOverAnimation(source, decoration, limit, signal);

  const image = source.frames[0];
  if (isPlain(decoration) && file) return fits(file, limit);

  if (!isAnimated(decoration)) {
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = Math.min(
      STILL_SIZE,
      Math.max(image.width, 128),
    );
    drawDecorated(canvas.getContext("2d")!, image, canvas.width, 0, decoration);
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("toBlob"))),
        "image/png",
      ),
    );
    return fits(new File([blob], "foto.png", { type: "image/png" }), limit);
  }

  // smaller each time it doesn't fit (rare: the effects change few pixels)
  for (const size of [ANIMATED_SIZE, 256, 192]) {
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = size;
    const context = canvas.getContext("2d", { willReadFrequently: true })!;
    const data: Uint8ClampedArray[] = [];
    for (let i = 0; i < LOOP_FRAMES; i++) {
      signal?.throwIfAborted();
      drawDecorated(context, image, size, i / LOOP_FRAMES, decoration);
      data.push(context.getImageData(0, 0, size, size).data);
    }
    const made = await encodeInWorker(
      { width: size, height: size, fps: loopFps(decoration), data },
      limit,
      () => {},
      "overlay",
      signal,
    );
    if (made.fits) return gifFile(made.bytes);
  }
  throw new Error("a animação ficou maior que o limite");
}

/**
 * Konkord: an animated picture (with or without a decoration) as a GIF.
 * Taken at a steady frame rate on the source's timeline: fast enough for
 * its shortest frame (so uneven delays keep their timing, longer frames
 * repeat), 20 fps when the decoration moves too. The encoder drops frames,
 * colours and then size until it fits.
 */
export async function renderOverAnimation(
  source: Source,
  decoration: Decoration | undefined,
  limit: number,
  signal?: AbortSignal,
) {
  const { width, height } = source.frames[0];
  const moving = decoration ? isAnimated(decoration) : false;
  const shortest = Math.min(
    ...source.ends.map((end, i) => end - (i ? source.ends[i - 1] : 0)),
  );
  let fps = moving
    ? OVER_ANIMATION_FPS
    : Math.min(OVER_ANIMATION_FPS, Math.max(2, 1000 / shortest));
  // a long, wide animation would take too much memory: fewer frames then
  const budget = Math.floor(MAX_PIXELS / (width * height));
  const wanted = Math.round((source.duration / 1000) * fps);
  if (wanted > budget) fps = (fps * budget) / wanted;
  const count = Math.max(2, Math.min(wanted, budget));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d", { willReadFrequently: true })!;
  const data: Uint8ClampedArray[] = [];
  for (let i = 0; i < count; i++) {
    signal?.throwIfAborted();
    const ms = (i * 1000) / fps;
    const image = frameAt(source, ms);
    if (decoration)
      drawDecorated(
        context,
        image,
        width,
        effectTime(source, decoration, ms),
        decoration,
      );
    else {
      context.clearRect(0, 0, width, height);
      context.drawImage(image, 0, 0, width, height);
    }
    data.push(context.getImageData(0, 0, width, height).data);
  }
  const made = await encodeInWorker(
    { width, height, fps, data },
    limit,
    () => {},
    "video",
    signal,
  );
  if (!made.fits) throw new Error("a animação ficou maior que o limite");
  return gifFile(made.bytes);
}

/** the file, or an error when it is over the upload limit */
function fits(file: File, limit: number) {
  if (file.size > limit) throw new Error("a foto ficou maior que o limite");
  return file;
}

function gifFile(bytes: Uint8Array) {
  return new File([bytes as BlobPart], "foto.gif", { type: "image/gif" });
}
